import areasJson from '../../../recipes/art/painted/areas.json' with { type: 'json' };
import placesJson from '../../../recipes/art/painted/places/areas.json' with { type: 'json' };
// One line per zone whose plates live under recipes/art/painted/zones/<zone>/.
import z01Json from '../../../recipes/art/painted/zones/Z01/areas.json' with { type: 'json' };
import z02Json from '../../../recipes/art/painted/zones/Z02/areas.json' with { type: 'json' };
import z06Json from '../../../recipes/art/painted/zones/Z06/areas.json' with { type: 'json' };
import mysteryJson from '../../../recipes/art/painted/mystery/areas.json' with { type: 'json' };
import type { Zone } from '../../../packages/contracts/src/index.ts';
import type { PaintedPlate, Point } from '../../../packages/view-phaser/src/painted-renderer.ts';
import { createCombat, type CombatState } from '../../../packages/sim/src/combat/index.ts';
import type { Prerequisites } from '../../../packages/sim/src/quests/graph.ts';
import type { WorldObject } from './world-objects.ts';
import { type LayerLink, type NavigationMap, type NavPosition, findPath } from '../../../packages/sim/src/navigation/index.ts';
import { createYardCombat, PC_ID, COMPANION_ID, BAILIFF_IDS, CELL } from './scene.ts';

export interface Area extends PaintedPlate {
  name: string;
  zone?: Zone;
  beatId?: string;
  anchor?: Point;
  spawn: Point;
  arrival: Point;
  /** A first life's opening here: the steps on the boat before the floor, and the camera's first view over the river. */
  opening?: { boat: readonly Point[]; view: Point };
  walkable: readonly (readonly Point[])[];
  blocked: readonly (readonly Point[])[];
  /** `beatId` scopes a landmark to one of the beats sharing this plate; unscoped, it serves them all. */
  landmarks: readonly { id: string; label: string; at: Point; text: string; to?: string; toBeat?: string; beatId?: string }[];
  /** Things in the painting that carry story actions (world-objects.ts). */
  objects?: readonly WorldObject[];
  /** `when`: world facts under which this person or prop is in the place (story.ts worldFacts); always, if absent. */
  people: readonly { id: string; appearance: string; name: string; route: readonly Point[]; when?: Prerequisites }[];
  props?: readonly (NonNullable<PaintedPlate['props']>[number] & { when?: Prerequisites })[];
}
export const AREAS = [...areasJson.areas, ...placesJson.areas, ...z01Json.areas, ...z02Json.areas, ...z06Json.areas, ...mysteryJson.areas].map(area => ({ ...area, image: area.image.replace(/^\//, '') })) as unknown as readonly Area[];
export const GRID = areasJson.gridPixels;
export const toPixel = (position: NavPosition): Point => [position.x / CELL * GRID, position.y / CELL * GRID];
export const toPosition = ([x, y]: Point): NavPosition => ({ x: Math.round(x / GRID) * CELL, y: Math.round(y / GRID) * CELL, layer: 0 });
export const SILT_STAIR_SHORTCUTS: Readonly<Record<'drain' | 'sluice', LayerLink>> = {
  drain: { from: toPosition([480, 608]), to: toPosition([624, 432]), cost: 8000, bidirectional: true },
  sluice: { from: toPosition([384, 608]), to: toPosition([1488, 480]), cost: 8000, bidirectional: true },
};
export function contains([x, y]: Point, polygon: readonly Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [ax, ay] = polygon[i]!, [bx, by] = polygon[j]!;
    if ((ay > y) !== (by > y) && x < (bx - ax) * (y - ay) / (by - ay) + ax) inside = !inside;
  }
  return inside;
}
export function navigation(area: Area): NavigationMap {
  const cells: { x: number; y: number }[] = [];
  for (let y = GRID; y < area.height; y += GRID) for (let x = GRID; x < area.width; x += GRID) {
    if (area.walkable.some(polygon => contains([x, y], polygon)) && !area.blocked.some(polygon => contains([x, y], polygon))) cells.push({ x: x / GRID * CELL, y: y / GRID * CELL });
  }
  return { cellSize: CELL, layers: [{ id: 0, cells }], links: area.id === 'silt-stair' ? Object.values(SILT_STAIR_SHORTCUTS) : [] };
}
export const MAPS = Object.fromEntries(AREAS.map(area => [area.id, navigation(area)]));
export function nearest(map: NavigationMap, point: Point, maxPixels = Infinity, occupied: readonly Point[] = [], accept?: (position: NavPosition) => boolean): NavPosition | null {
  let closest: NavPosition | null = null, distance = maxPixels;
  for (const cell of map.layers[0]!.cells) {
    const position = { ...cell, layer: 0 }, [x, y] = toPixel(position);
    if (occupied.some(([ox, oy]) => Math.hypot(x - ox, y - oy) < 32)) continue;
    const d = Math.hypot(x - point[0], y - point[1]);
    if (d < distance && (!accept || accept(position))) { distance = d; closest = position; }
  }
  return closest;
}
export function createAreaCombat(area: Area): CombatState {
  const map = MAPS[area.id]!;
  const original = createYardCombat();
  const placements = area.id === 'silt-stair'
    ? [area.spawn, [368, 592], [1088, 240], [1120, 272]] as const
    : [area.spawn, [area.spawn[0] - 32, area.spawn[1]], [848, 720], [864, 736]] as const;
  const ids = [PC_ID, COMPANION_ID, ...BAILIFF_IDS];
  return createCombat(map, ids.map((id, i) => ({ ...original.actors[id]!,
    id: area.id === 'silt-stair' && i > 1 ? ['person:yard-bailiff-axe', 'person:yard-bailiff-shield'][i - 2]! : id,
    position: nearest(map, placements[i]!)!, movePerTick: 1000, attackRange: 2000 })), PC_ID);
}

/** Travellers retain damage, death, and PC command sequence. Local actors stay in their area. */
export function travelState(from: CombatState, to: CombatState, area: Area): CombatState {
  if (from.actors[PC_ID]!.status === 'dead') throw new Error('The dead cannot travel');
  if (from.paused) throw new Error('Resume before travelling');
  const next = structuredClone(to);
  next.tick = from.tick; next.lastPcSequence = from.lastPcSequence;
  next.acceptedPcIntents = [...from.acceptedPcIntents]; next.pcQueue = []; next.paused = false;
  [PC_ID, COMPANION_ID].forEach((id, i) => {
    const actor = from.actors[id]!;
    next.actors[id] = { ...structuredClone(actor), activeAction: null,
      position: actor.status === 'dead' && to.actors[id]?.status === 'dead'
        ? { ...to.actors[id]!.position }
        : nearest(MAPS[area.id]!, [area.arrival[0] - i * 32, area.arrival[1]])! };
  });
  return next;
}

export function patrolRoute(area: Area, points: readonly Point[]): readonly Point[] {
  const map = MAPS[area.id]!, result: Point[] = [];
  const positions = points.map(point => nearest(map, point)!);
  for (let i = 0; i < positions.length - 1; i++) {
    const path = findPath(map, positions[i]!, positions[i + 1]!);
    if (path) result.push(toPixel(positions[i]!), ...path.map(step => toPixel(step.position)));
  }
  return result.length ? result : [toPixel(positions[0]!)];
}
